/**
 * Storage layer — replaces Firebase with our Cloudflare R2 backend.
 * Keeps the same export signatures so the rest of the app doesn't change.
 */
import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { FILE_CACHE_MAX_AGE_SEC } from "../app_constants";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";

// The base URL for our Cloudflare Worker API
const API_BASE = import.meta.env.VITE_APP_BACKEND_URL || "";
const AUTH_TOKEN = import.meta.env.VITE_APP_AUTH_TOKEN || "";

function authHeaders(): Record<string, string> {
  if (!AUTH_TOKEN) return {};
  return { Authorization: `Bearer ${AUTH_TOKEN}` };
}

// ---------------------------------------------------------------------------
// Scene version cache (mirrors the old FirebaseSceneVersionCache)
// ---------------------------------------------------------------------------

class SceneVersionCache {
  private static cache = new Map<string, number>();
  static get = (roomId: string) => SceneVersionCache.cache.get(roomId);
  static set = (roomId: string, elements: readonly SyncableExcalidrawElement[]) => {
    SceneVersionCache.cache.set(roomId, getSceneVersion(elements));
  };
}

// ---------------------------------------------------------------------------
// Encrypt / Decrypt helpers
// ---------------------------------------------------------------------------

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);
  return { ciphertext: encryptedBuffer, iv };
};

type StoredScene = {
  sceneVersion: number;
  iv: number[];
  ciphertext: number[];
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = new Uint8Array(data.ciphertext).buffer as ArrayBuffer;
  const iv = new Uint8Array(data.iv);
  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(new Uint8Array(decrypted));
  return JSON.parse(decodedData);
};

// ---------------------------------------------------------------------------
// Public API — same signatures as the old firebase.ts exports
// ---------------------------------------------------------------------------

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.roomId) === sceneVersion;
  }
  return true;
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const cleanPrefix = prefix.replace(/^\//, "");
        const resp = await fetch(`${API_BASE}/api/files/${cleanPrefix}/${id}`, {
          method: "PUT",
          body: buffer.buffer as ArrayBuffer,
          headers: authHeaders(),
        });
        if (!resp.ok) {
          throw new Error(`Upload failed: ${resp.status}`);
        }
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey } = portal;
  if (!roomId || !roomKey || isSavedToFirebase(portal, elements)) {
    return null;
  }

  // Load existing scene from R2 (if any) for reconciliation
  let reconciledElements: SyncableExcalidrawElement[] = elements as SyncableExcalidrawElement[];

  try {
    const resp = await fetch(`${API_BASE}/api/scenes/${roomId}`);
    if (resp.ok) {
      const buffer = await resp.arrayBuffer();
      const storedScene: StoredScene = JSON.parse(
        new TextDecoder().decode(new Uint8Array(buffer)),
      );
      const prevElements = getSyncableElements(
        restoreElements(await decryptElements(storedScene, roomKey), null),
      );
      reconciledElements = getSyncableElements(
        reconcileElements(
          elements,
          prevElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
    }
  } catch {
    // No existing scene or fetch failed — just save what we have
  }

  const { ciphertext, iv } = await encryptElements(roomKey, reconciledElements);
  const storedScene: StoredScene = {
    sceneVersion: getSceneVersion(reconciledElements),
    iv: Array.from(iv),
    ciphertext: Array.from(new Uint8Array(ciphertext)),
  };

  const saveResp = await fetch(`${API_BASE}/api/scenes/${roomId}`, {
    method: "PUT",
    body: JSON.stringify(storedScene),
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });

  if (!saveResp.ok) {
    throw new Error(`Scene save failed: ${saveResp.status}`);
  }

  SceneVersionCache.set(roomId, reconciledElements);

  return toBrandedType<RemoteExcalidrawElement[]>(reconciledElements);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  _socket: unknown,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  try {
    const resp = await fetch(`${API_BASE}/api/scenes/${roomId}`);
    if (!resp.ok) {
      return null;
    }
    const buffer = await resp.arrayBuffer();
    const storedScene: StoredScene = JSON.parse(
      new TextDecoder().decode(new Uint8Array(buffer)),
    );
    const elements = getSyncableElements(
      restoreElements(await decryptElements(storedScene, roomKey), null, {
        deleteInvisibleElements: true,
      }),
    );

    SceneVersionCache.set(roomId, elements);
    return elements;
  } catch {
    return null;
  }
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const cleanPrefix = prefix.replace(/^\//, "");
        const response = await fetch(
          `${API_BASE}/api/files/${cleanPrefix}/${id}`,
        );
        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            { decryptionKey },
          );
          const dataURL = new TextDecoder().decode(data) as DataURL;
          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};

// Keep this export for compat — it's a no-op now
export const loadFirebaseStorage = async () => {
  return null;
};
